// ------------------------------------------------------------
// preProcessPropagate.js
// ------------------------------------------------------------
// Group-level macro expansion for Oscilla DSL.
//
// Supports:
//   propagate(
//     osc(freq:${1}, trig:edge),
//     scale(values:[1,1.2,1], dur:${2}),
//     rnd(300,800),
//     rnd(2,6)
//   )
//
// Behaviour:
//   • Multiple cue templates per child
//   • rnd(...) and numbers are compile-time expressions ONLY
//   • Exactly ONE uid per child, shared across cues
//   • Space-separated cue IDs (parser-safe)
//   • Nested propagate(...) supported (multi-pass)
// ------------------------------------------------------------


// ------------------------------------------------------------
// Expression evaluator (compile-time only)
// Exported for the DSL inspector's propagate editor (live preview).
// ------------------------------------------------------------
import { expandPatternFills } from "./preProcessPattern.js";
import { PATTERN_NAMES, PATTERN_WORDS, RANDOM_CALLS } from "./parser.js";
import { splitArgs } from "./dslWrite.js";
import { FOLLOW_FN, LINK_FN, MAP_FN } from "./parserSignalRef.js";
import { MOD_SHAPES } from "../cues/audio/modShapes.js";

export function evaluateExpr(rawExpr) {
  const expr = String(rawExpr).trim();

  // rnd([a,b,c]) — pick one item (numbers or literals like colour names)
  let m = expr.match(/^rnd\s*\(\s*\[(.+)\]\s*\)\s*$/);
  if (m) {
    const items = m[1].split(",").map(s => s.trim());
    const choice = items[Math.floor(Math.random() * items.length)];
    const num = Number(choice);
    return Number.isNaN(num) ? choice : num;
  }

  // rnd(min,max) — uniform float
  m = expr.match(/^rnd\s*\(\s*([+-]?\d*\.?\d+)\s*,\s*([+-]?\d*\.?\d+)\s*\)\s*$/);
  if (m) {
    const min = parseFloat(m[1]);
    const max = parseFloat(m[2]);
    return min + Math.random() * (max - min);
  }

  // irnd(min,max) — uniform integer, inclusive
  m = expr.match(/^irnd\s*\(\s*([+-]?\d+)\s*,\s*([+-]?\d+)\s*\)\s*$/);
  if (m) {
    const min = Math.ceil(parseFloat(m[1]));
    const max = Math.floor(parseFloat(m[2]));
    return min + Math.floor(Math.random() * (max - min + 1));
  }

  // plain number
  if (/^[+-]?\d*\.?\d+$/.test(expr)) {
    return parseFloat(expr);
  }

  // fallback literal
  return expr;
}


// ------------------------------------------------------------
// Split top-level args: commas outside brackets and quotes, so
// text(content:"a, b") stays one template (dslWrite.js splitArgs).
// Exported for the DSL inspector's propagate editor.
// ------------------------------------------------------------
export const splitTopLevelArgs = splitArgs;


// ------------------------------------------------------------
// Placeholder expansion: ${1}, ${2}, ...
// Each occurrence re-evaluates rnd(...)
// ------------------------------------------------------------
function applyPlaceholders(template, argExprs) {
  let expanded = template;

  argExprs.forEach((expr, idx) => {
    const n = idx + 1;
    const re = new RegExp(`\\$\\{${n}\\}`, "g");

    expanded = expanded.replace(re, () => {
      const val = evaluateExpr(expr);
      return String(val);
    });
  });

  return expanded;
}


// ------------------------------------------------------------
// Cue template detection
// Exported for the DSL inspector's propagate editor.
// ------------------------------------------------------------
// IMPORTANT:
//   • osc(...), scale(...), rotate(...), o2p(...), propagate(...)
//     → templates
//   • rnd(...) / irnd(...) → compile-time expressions (never emitted)
//   • the parser's patterns (Pseq, Pwhite, …), its random calls (rand,
//     choose, …), follow()/map()/mul()/add() and the sinOsc() family
//     → runtime expressions passed through VERBATIM into the template,
//       so each child gets its own live pattern/random — must never be
//       mistaken for cue templates
// ------------------------------------------------------------
const VALUE_CALLS = [...RANDOM_CALLS, "exprand", FOLLOW_FN, LINK_FN, MAP_FN, "mul", "add", ...MOD_SHAPES];
const NON_TEMPLATE_CALL_RE = new RegExp(`^(?:${[...PATTERN_NAMES, ...PATTERN_WORDS, ...VALUE_CALLS].join("|")})\\s*\\(`, "i");

export function isCueTemplate(str) {
  return (
    /^[a-zA-Z_][a-zA-Z0-9_-]*\s*\(/.test(str) &&
    !NON_TEMPLATE_CALL_RE.test(str)
  );
}


// ------------------------------------------------------------
// Where a propagate() template lives
// ------------------------------------------------------------
// In `data-oscilla`, beside any other cues the group carries. An SVG id is an
// XML ID: it cannot contain "(", ":", "," or spaces, so DSL in an id is
// invalid markup that only worked because nothing validated it. The id stays
// a plain name.
//
// Older scores carry the template in the id. They load
// unchanged, and the next save through the editor migrates them (the group is
// marked with data-oscilla-propagate-legacy while expanded, so the save path
// knows the file still keys that element by its template).
export const PROPAGATE_SELECTOR = '[data-oscilla*="propagate("], [id^="propagate("]';

/** Split "a(…) b(…)" into top-level expressions; spaces inside brackets and quotes stay put. */
function splitCueExprs(str) {
  const out = [];
  let depth = 0, start = 0, quote = "";
  for (let i = 0; i < str.length; i++) {
    const c = str[i];
    if (quote) { if (c === quote) quote = ""; continue; }
    if (c === '"' || c === "'") quote = c;
    else if (c === "(" || c === "[") depth++;
    else if (c === ")" || c === "]") depth--;
    else if (depth === 0 && /\s/.test(c)) {
      const piece = str.slice(start, i).trim();
      if (piece) out.push(piece);
      start = i + 1;
    }
  }
  const last = str.slice(start).trim();
  if (last) out.push(last);
  return out;
}

/**
 * The propagate() template this element carries, whatever else it carries, and
 * which attribute it came from. null when it carries none.
 * @returns {{template:string, rest:string, fromId:boolean}|null}
 */
export function readPropagateSource(el) {
  const id = el?.getAttribute?.("id") || "";
  if (/^propagate\s*\(/.test(id)) return { template: id, rest: "", fromId: true };
  const data = el?.getAttribute?.("data-oscilla") || "";
  if (!data.includes("propagate(")) return null;
  const parts = splitCueExprs(data);
  const i = parts.findIndex(p => /^propagate\s*\(/.test(p));
  if (i < 0) return null;
  return {
    template: parts[i],
    rest: parts.filter((_, n) => n !== i).join(" "),
    fromId: false,
  };
}


// ------------------------------------------------------------
// Main propagate macro
// ------------------------------------------------------------
export function propagate(svgRoot) {
  if (!svgRoot) return;
  // A pattern-filled shape carrying a propagate() id first becomes a group
  // of the pattern's objects (preProcessPattern.js); the group takes the id
  // and is expanded below like any other.
  expandPatternFills(svgRoot);

  let expandedSomething;

  // 🔁 Multi-pass expansion to support nested propagate(...)
  do {
    expandedSomething = false;

    const groups = svgRoot.querySelectorAll(PROPAGATE_SELECTOR);
    if (!groups.length) return;

    console.info(`[propagate] Found ${groups.length} groups`);

    // Each group's number, for its members' uids (prop_<n>_<i>): its index in
    // the first pass of the first call; counted on from there in later passes
    // and calls, so a nested group or another group expanded later never takes
    // a number already given; and kept on the group, so a re-expansion from
    // the editor gives its members the uids they had.
    const base = svgRoot._propagateCount ?? 0;
    svgRoot._propagateCount = base + groups.length;

    groups.forEach((group, groupIndex) => {
      const src = readPropagateSource(group);
      if (!src) return;                       // already expanded, or not ours
      group._propagateNum ??= base + groupIndex;
      const { template: id, rest, fromId } = src;

      // Park the template and take it out of the attribute it was read from,
      // whatever happens next. A group that cannot expand must still stop
      // matching the selector, or the multi-pass loop below finds it again on
      // every pass, forever (e.g. a template on a lone <circle>), freezing the
      // tab on load. Either way the template stays in
      // data-oscilla-propagate-id, so the DSL editor can show, fix or remove it.
      const park = () => {
        group.setAttribute('data-oscilla-propagate-id', id);
        if (fromId) {
          // Legacy file: it is keyed on disk by this template until the next
          // save migrates it. The group still needs AN id: without one it
          // vanishes from the inspector's target breadcrumb, and there is no
          // way back from a member to the template.
          group.setAttribute('data-oscilla-propagate-legacy', '1');
          group.setAttribute('id', plainGroupId(id, groupIndex, svgRoot));
        } else {
          if (rest) group.setAttribute('data-oscilla', rest);
          else group.removeAttribute('data-oscilla');
          if (!group.getAttribute('id')) {
            group.setAttribute('id', plainGroupId(id, groupIndex, svgRoot));
          }
        }
        // Which element the FILE keys this template by, so the editor can save
        // it: the group's own id; the pattern host it was generated from (a
        // pattern group exists only at runtime, never on disk); or, until the
        // next save migrates the file, the template text itself.
        const patternHost = group._oscillaPatternHost;
        group.setAttribute('data-oscilla-diskid',
          fromId ? id : (patternHost?.getAttribute('id') || group.getAttribute('id')));
      };

      const retire = (why) => {
        console.warn(`[propagate] ⚠️ ${why} — not expanding: ${id}`);
        park();
      };

      const match = id.match(/^propagate\((.*)\)$/s);
      if (!match) { retire('malformed propagate() template'); return; }

      const inner = match[1];
      const parts = splitTopLevelArgs(inner);
      if (!parts.length) { retire('empty propagate()'); return; }

      // Separate cue templates from argument expressions
      const templates = parts.filter(isCueTemplate);
      const argExprs  = parts.filter(p => !isCueTemplate(p));

      // Direct children -- except that after animationAssign a leaf member
      // sits inside a <g class="oscilla-anim"> transform wrapper (animShared.js),
      // and on re-expansion that wrapper is what group.children returns. The
      // member is the wrapper's one element; the wrapper itself never is.
      const children = Array.from(group.children).map(memberOf);
      if (!children.length) { retire('no children inside group'); return; }

      children.forEach((child, i) => {
        // 1) Expand placeholders for each cue
        const expandedCues = templates.map(tpl =>
          applyPlaceholders(tpl, argExprs)
        );

        // 2) One UID per child (shared across cues)
        const uid = `prop_${group._propagateNum}_${i}`;

        // 3) Inject uid into each cue
        const withUid = expandedCues.map(cue => {
          if (/uid\s*:/.test(cue)) {
            // /g: a space-separated compound template ("color(...) scale(...)")
            // is ONE template string carrying several uid:s — every cue's uid
            // needs the child suffix, or all children collide in the registry
            return cue.replace(
              /uid\s*:\s*([A-Za-z0-9_-]+)/g,
              (_, base) => `uid:${base}_${i}`
            );
          }
          return cue.replace(/\)$/, `, uid:${uid})`);
        });

        // 4) The expanded DSL goes into data-oscilla, which animationAssign and
        //    assignCues read first. The member KEEPS its own id: writing the
        //    DSL into id would clobber whatever the author named it, so an o2p
        //    path, a trans(to:) target, a bounds selector or an annotation
        //    that referenced the member would break while expanded.
        //    A data-oscilla the member had of its own is kept aside so
        //    reexpandPropagateGroup can put it back -- re-expansion is lossless.
        const expandedDsl = withUid.join(" ");
        if (child.hasAttribute('data-oscilla') && !child.hasAttribute('data-oscilla-propagate-orig')) {
          child.setAttribute('data-oscilla-propagate-orig', child.getAttribute('data-oscilla'));
        }
        // A propagate() of the child's OWN survives the overwrite: it is a
        // nested cluster, and the pass that expands it has to be able to find
        // it in data-oscilla. (In the legacy form it lives in the child's id
        // and is never touched.)
        const nested = readPropagateSource(child);
        const keep = nested && !nested.fromId ? nested.template : '';
        child.setAttribute('data-oscilla', keep ? `${keep} ${expandedDsl}` : expandedDsl);
        // Mark child as propagate-expanded so inspector can trace back to parent
        child.setAttribute('data-oscilla-propagate-child', 'true');

        console.debug(
          `[propagate] #${i} → data-oscilla="${expandedDsl}"`
        );
      });

      // The template moves to data-oscilla-propagate-id, where the DSL
      // inspector shows, edits and saves it. It cannot stay in data-oscilla:
      // propagate() is a macro, not a cue, and assignCues would try to parse
      // it as one — and the group's own cues live there.
      park();
      // Only a real expansion earns another pass (nested propagate() groups).
      expandedSomething = true;
    });

  } while (expandedSomething);
}


// ------------------------------------------------------------
// Global exposure (legacy compatibility)
// ------------------------------------------------------------
if (typeof window !== "undefined") {
  window.propagate = propagate;
}

// ------------------------------------------------------------
// reexpandPropagateGroup
// Live re-expansion of a single propagate group without page reload.
// Used by the DSL inspector when the user edits and saves a propagate template.
//
// Caller must put the new propagate DSL back where propagate() reads it —
// data-oscilla (or, for a file not yet migrated, the id) — before calling.
//
// Uses dynamic imports for animationAssign / assignCues to avoid the
// circular dependency: cueDispatcher → preProcessPropagate → cueDispatcher.
// ------------------------------------------------------------
// Undo an expansion in the live DOM: stop the members' animations, drop them
// from the registries, and put each member back the way it was authored.
// The members are the MARKED elements: after animationAssign a leaf member
// sits inside a <g class="oscilla-anim"> wrapper, so groupEl.children would
// be the wrappers, not the members.
// Members of a nested propagate() group belong to that group. The group may
// carry its template in data-oscilla-propagate-id or (mid-save) in id, so the
// owner is matched on either form. Used by reexpandPropagateGroup, and by the
// DSL inspector's Remove all, which then leaves the group as a plain <g>.
export function unexpandPropagateGroup(groupEl) {
  groupEl = groupEl._oscillaPatternGroup || groupEl;      // a pattern host's members live in its group
  const members = Array.from(groupEl.querySelectorAll('[data-oscilla-propagate-child]'))
    .filter(m => m.parentElement?.closest(`[data-oscilla-propagate-id], ${PROPAGATE_SELECTOR}`) === groupEl);
  for (const child of members) {
    child._oscillaColorAnim?.stop?.();
    if (child._oscillaScaleAnim?.stop) child._oscillaScaleAnim.stop();
    clearTimeout(child._oscillaScaleAnim);
    if (child._oscillaRotateAnim?.stop) child._oscillaRotateAnim.stop();
    clearTimeout(child._oscillaRotateAnim);
    clearInterval(child._blinkTimer);
    for (const [uid, rec] of Object.entries(window.oscillaAnimRegistry || {})) {
      if (rec.el === child) {
        rec.cfg?._anim?.stop?.();
        delete window.oscillaAnimRegistry[uid];
        window.runningAnimations?.delete(uid);
      }
    }
    if (window.cues) {
      window.cues = window.cues.filter(c => c.element !== child);
    }
    // The member keeps its id (see propagate()); only the expansion goes,
    // and a data-oscilla the member had of its own comes back.
    const orig = child.getAttribute('data-oscilla-propagate-orig');
    if (orig !== null) {
      child.setAttribute('data-oscilla', orig);
      child.removeAttribute('data-oscilla-propagate-orig');
    } else {
      child.removeAttribute('data-oscilla');
    }
    child.removeAttribute('data-oscilla-propagate-child');
  }
  return members.length;
}

export async function reexpandPropagateGroup(groupEl) {
  const svg = groupEl.closest('svg') || document.querySelector('svg');
  if (!svg) return;

  // 1. Put the members back as authored (see unexpandPropagateGroup)
  unexpandPropagateGroup(groupEl);

  // 2. Re-run propagate on the full SVG (PROPAGATE_SELECTOR finds this group)
  propagate(svg);

  // 3. Re-run animation assignment and cue registration scoped to this group only.
  //    Dynamic imports break the cueDispatcher → preProcessPropagate → cueDispatcher cycle.
  // A pattern-filled shape that has just been given propagate() was turned
  // into a member group by the pass above: that group is what gets assigned.
  groupEl = groupEl._oscillaPatternGroup || groupEl;
  const { animationAssign } = await import('../cues/animation.js');
  const { assignCues }      = await import('../cues/cueDispatcher.js');

  animationAssign(groupEl);

  const newCues = [];
  await assignCues(groupEl, newCues);
  if (!window.cues) window.cues = [];
  const existingEls = new Set(window.cues.map(c => c.element));
  for (const c of newCues) {
    if (!existingEls.has(c.element)) window.cues.push(c);
  }
}

// A plain, stable id for an expanded propagate() group: "propagate-<uid>"
// from the template's first uid:, else the group's index, suffixed if that
// id is already taken. Stable across re-expansion, since the same template
// yields the same uid, and free of "(" so no id-based cue scanner sees it.
function plainGroupId(templateId, groupIndex, svgRoot) {
  const m = templateId.match(/uid\s*:\s*([A-Za-z0-9_-]+)/);
  const base = `propagate-${m ? m[1] : groupIndex}`;
  let candidate = base, n = 2;
  while (svgRoot.querySelector(`#${CSS.escape(candidate)}`)) candidate = `${base}-${n++}`;
  return candidate;
}


// The member behind a direct child of a propagate() group: a leaf member's
// <g class="oscilla-anim"> transform wrapper holds exactly one element.
function memberOf(node) {
  return node.classList?.contains('oscilla-anim') ? (node.firstElementChild || node) : node;
}
